"use client";

import { ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";
import { useReadContract } from "wagmi";
import { EXPECTED_CHAIN_ID } from "@/configs/expected-chain";
import { DAO_ABI } from "@/contract/abi/dao";
import { ContractAddress } from "@/contract/addresses";
import { useExecutorFromChain } from "@/hooks/dao/useExecutorFromChain";
import { useIsDaoMember } from "@/hooks/dao/useIsDaoMember";
import { useGoBack } from "@/hooks/useGoBack";
import { localize } from "@/lib/utils/localize";
import type { components } from "@/types/api/api-types";
import { ActBody } from "../dao/proposals/detail/ActBody";
import { BottomCard } from "../dao/proposals/detail/BottomCard";
import { MetaRow } from "../dao/proposals/detail/MetaRow";
import type { StatusChipState } from "../dao/proposals/shared/StatusChip";
import { BlogViewPing } from "./BlogViewPing";

const QUORUM = 15;

type BlogPost = components["schemas"]["BlogPostDetailResponseDto"];
export type DaoBlogPost = BlogPost & { category: "dao_action" };

export function PublicDaoActionArticle({ post }: { post: DaoBlogPost }) {
  return <DaoActionDetail post={post} />;
}

function DaoActionDetail({ post }: { post: DaoBlogPost }) {
  const t = useTranslations("dao");
  const tc = useTranslations("common");
  const goBack = useGoBack("/blog?category=dao_action");
  const { data: isMember } = useIsDaoMember();
  const txIndex = post.index ?? -1;

  const chainTx = useReadContract({
    abi: DAO_ABI,
    address: ContractAddress.DAO,
    chainId: EXPECTED_CHAIN_ID,
    functionName: "getTransaction",
    args: [BigInt(txIndex)],
    query: { enabled: Number.isFinite(txIndex) && txIndex >= 0, retry: false },
  });

  const txData = chainTx.isError ? undefined : chainTx.data;
  const chainExecuted = txData ? txData[3] : false;
  const needsChainExecutor =
    chainTx.isSuccess && chainExecuted && post.executor === null;
  const chainExec = useExecutorFromChain(txIndex, needsChainExecutor);

  const numConfirmations = txData ? Number(txData[5]) : 0;
  const isExecuted = post.executed_at !== null || chainExecuted;
  const isQuorumReached = numConfirmations >= QUORUM;

  const executorPayload = post.executor
    ? {
        executorAddress: post.executor.wallet_address,
        executorNickname: post.executor.nickname,
      }
    : chainExec.executorAddress
      ? {
          executorAddress: chainExec.executorAddress,
          executorNickname: chainExec.nickname,
        }
      : null;

  const variant: "v1" | "v2" | "v3" = isExecuted
    ? "v3"
    : isQuorumReached
      ? "v2"
      : "v1";

  const chipState: StatusChipState = isExecuted
    ? "executed"
    : isQuorumReached
      ? "not_executed"
      : "not_approved";

  const accentVariant = variant === "v2" || variant === "v3";
  const title = localize(post.name, t("proposals.detail.untitled"));
  const description = localize(post.description);

  return (
    <div className="flex flex-col gap-6 px-4 py-6 max-w-2xl mx-auto">
      <BlogViewPing slug={post.slug} />

      <button
        type="button"
        onClick={goBack}
        aria-label={t("proposals.detail.backAria")}
        className="inline-flex items-center gap-1 self-start text-sm text-text-secondary hover:text-text-primary cursor-pointer"
      >
        <ChevronRight size={20} aria-hidden="true" />
        <span>{tc("back")}</span>
      </button>

      <MetaRow
        title={title}
        viewCount={post.view_count}
        numConfirmations={numConfirmations}
        chipState={chipState}
        accentVariant={accentVariant}
        txIndex={txIndex}
        cycleCount={post.cycle_count ?? 0}
      />

      <ActBody
        submitterAddress={post.submitter?.wallet_address ?? ""}
        submitterNickname={post.submitter?.nickname ?? null}
        createdAt={post.created_at}
        contractAddress={post.contract_address ?? ""}
        functionSignature={post.function_signature ?? ""}
        inputs={post.inputs}
        description={description}
      />

      <BottomCard
        variant={variant}
        isMember={isMember === true}
        txIndex={txIndex}
        executor={executorPayload}
      />
    </div>
  );
}
